import { MethodButton, StyledAuth } from "./style";
import { MyButton, MyText } from "../../components";
import { Col, Flex, Form, Input, Row, message, Grid, Modal } from "antd";
import { LogoIcon } from "../../assets";
import { useState, useEffect, useCallback } from "react";
import { KeyOutlined, MailOutlined, PhoneOutlined, GoogleOutlined, MobileOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { LANG_KEY } from "../../utils/constants/language";
import { MySegmented } from "../../components";
import { StyledCheckbox } from './style';
import { RegistrationSteps } from "../../components/common/MySteps";
import { RegisterVerification } from "../RegisterVerification/index.tsx";
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { fetchRegister, ROUTE_PATH, selectStore, setRefreshTokenCookie, setTokenCookie } from "../../utils/"
import { useLang } from "../../utils/context/lang";
import InputMask from "react-input-mask";
import { clearRegisterData } from "../../utils/";
import { useEmbeddedLink } from "../../utils/context/embed/useEmbeddedLink.ts";
import { useEmbeddedNavigate } from "../../utils/context/embed/useEmbed.ts";
import styled from "styled-components";

const GOOGLE_CLIENT_ID =
  "31515366478-1jkjommo51mms631cc1k05lbk2hger0m.apps.googleusercontent.com";


function openGoogleOAuth(callback: (credential: string) => void) {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: `${window.location.origin}/auth/google/callback`,
    response_type: "token id_token",
    scope: "openid email profile",
    nonce: Math.random().toString(36).slice(2),
    prompt: "select_account",
  });

  const url = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  const popup = window.open(url, "googleAuth", "width=500,height=600,scrollbars=yes");

  const handler = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type === "GOOGLE_AUTH" && event.data?.id_token) {
      window.removeEventListener("message", handler);
      popup?.close();
      callback(event.data.id_token);
    }
  };
  window.addEventListener("message", handler);

  const timer = setInterval(() => {
    try {
      if (!popup || popup.closed) {
        clearInterval(timer);
        window.removeEventListener("message", handler);
        return;
      }
      const hash = popup.location.hash;
      if (hash) {
        const params = new URLSearchParams(hash.replace("#", ""));
        const idToken = params.get("id_token");
        if (idToken) {
          clearInterval(timer);
          window.removeEventListener("message", handler);
          popup.close();
          callback(idToken);
        }
      }
    } catch {
    }
  }, 500);
}

export const RegisterPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const [methodModalOpen, setMethodModalOpen] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState<null | "email" | "phone">(null);

  const { handleChangeLang, lang } = useLang();
  const navigate = useNavigate();
  const embeddedNavigate = useEmbeddedNavigate();
  const dispatch = useDispatch();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { t } = useTranslation("common");
  const { profile: { registerData: registerDataState } } = useSelector(selectStore);
  const [form] = Form.useForm();
  const embeddedLink = useEmbeddedLink();

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    username: '',
    password: '',
    type: 1,
    password_repeat: '',
  });

  const [isFormComplete, setIsFormComplete] = useState(false);

  const handleChangeLangSelect = useCallback((value: any) => {
    handleChangeLang(value);
  }, []);

  const API_BASE_AUTH = process.env.REACT_APP_AUTH;

  const handleGoogle = async (response: { credential: string }) => {
    try {
      const res = await fetch(`${API_BASE_AUTH}/rest/v1/user/google-auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: response.credential }),
    });

    const data = await res.json();

    if (data.status === "success") {
      setTokenCookie(data.access_token);
      setRefreshTokenCookie(data.refresh_token);

      if (data.is_new_user) {
        message.success(t("welcomeNewUser") || "Добро пожаловать! 🎉");
      }

      window.location.href = "/";
    } else {
      message.error(data.message || t("registrationError"));
    }
  } catch {
    message.error(t("registrationError"));
  }
};

  const handleSelectMethod = (method: "google" | "email" | "phone") => {
    if (method === "google") {
      setMethodModalOpen(false);
      openGoogleOAuth((id_token) => handleGoogle({ credential: id_token }));
      return;
    }
    const isPhone = method === "phone";
    setIsAnonymous(isPhone);
    form.setFieldsValue({ anonymous: isPhone });
    setSelectedMethod(method);
    setMethodModalOpen(false);
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    if (name === "phone") {
      let cleanedValue = value.replace(/\D/g, "");
      if (cleanedValue.length > 11) cleanedValue = cleanedValue.slice(0, 11);
      setFormData((prevState) => ({
        ...prevState,
        [name]: cleanedValue,
        username: isAnonymous ? cleanedValue : prevState.username,
        type: isAnonymous ? 2 : 1,
      }));
    } else {
      setFormData((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  const handleCheckboxChange = (e) => {
    setIsAnonymous(e.target.checked);
    if (e.target.checked) {
      setFormData(prevState => ({ ...prevState, email: '' }));
    } else {
      setFormData(prevState => ({ ...prevState, phone: '' }));
    }
  };

  const isFormValid = () => {
    const { email, phone, password, password_repeat } = formData;
    const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,10}$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    const cleanedPhone = phone.replace(/\D/g, "");
    const isEmailValid = isAnonymous ? cleanedPhone.length === 11 : emailRegex.test(email);
    const isPasswordValid = passwordRegex.test(password) && password === password_repeat;
    return isEmailValid && isPasswordValid;
  };

  useEffect(() => {
    setIsFormComplete(isFormValid());
  }, [formData, isAnonymous]);

  const handleRegister = async () => {
    try {
      const cleanedPhone = isAnonymous ? formData.phone.replace(/\D/g, "") : formData.phone;
      const cleanedUsername = isAnonymous ? cleanedPhone : formData.email;

      const payload = {
        ...formData,
        phone: undefined,
        email: undefined,
        username: cleanedUsername,
        password_repeat: undefined,
        type: isAnonymous ? 2 : 1
      };

      const response = await dispatch(fetchRegister(payload));
      if (response?.payload?.status === "error") {
        message.error(t("registrationError"));
        return;
      }
      if (response?.payload?.status === "success") {
        setIsExiting(true);
        setTimeout(() => {
          embeddedNavigate("/register/verification", { state: { username: cleanedUsername } });
        }, 300);
      }
    } catch (error) {
      message.error(t("registrationError"));
    }
  };

  useEffect(() => {
    if (registerDataState?.success) {
      embeddedNavigate('/register/verification');
    } else if (registerDataState?.failed && registerDataState?.loading) {
      message.error(t("registrationError"));
    }
  }, [registerDataState]);

  useEffect(() => {
    form.setFields([
      { name: "email", value: "", errors: [] },
      { name: "phone", value: "", errors: [] },
    ]);
  }, [isAnonymous]);

  if (isRegistered) {
    return <RegisterVerification username={formData.username} />;
  }

  const showForm = selectedMethod === "email" || selectedMethod === "phone";

  return (
    <>
      <Modal
        open={methodModalOpen}
        onCancel={() => setMethodModalOpen(false)}
        footer={null}
        centered
        width={460}
        styles={{ content: { borderRadius: 20, padding: "36px 32px" } }}
        title={
          <MyText color="#0A033C" size={22} weight={700} lineHeight="30px">
            {t("chooseRegisterMethod") || "Способ регистрации"}
          </MyText>
        }
      >
        <Flex vertical gap={12} style={{ marginTop: 16 }}>
          <MethodButton onClick={() => handleSelectMethod("google")}>
            <GoogleOutlined style={{ color: "#4285F4" }} />
            {t("loginWithGoogle") || "Войти через Google"}
          </MethodButton>

          <MethodButton onClick={() => handleSelectMethod("email")}>
            <MailOutlined style={{ color: "#008DD2" }} />
            {t("loginWithEmail") || "Войти с эл. адресом"}
          </MethodButton>

          <MethodButton onClick={() => handleSelectMethod("phone")}>
            <MobileOutlined style={{ color: "#008DD2" }} />
            {t("loginWithPhone") || "Войти с номером телефона"}
          </MethodButton>
        </Flex>
      </Modal>

      <StyledAuth className={isExiting ? 'fade-out' : 'fade-in'}>
        <div style={{ position: 'absolute', top: screens.xs ? 15 : 40, right: 40 }}>
          <MySegmented
            onChange={(value) => handleChangeLangSelect(value)}
            value={lang}
            options={[
              { label: 'ҚАЗ', value: LANG_KEY.KZ },
              { label: 'РУС', value: LANG_KEY.RU },
            ]}
          />
        </div>

        <Flex justify="center" align='center' style={{ width: 500 }}>
          <Row justify='center' gutter={[16, 16]}>
            <Col>
              <Link to={embeddedLink(ROUTE_PATH.MAIN)} onClick={() => dispatch(clearRegisterData())}>
                <LogoIcon />
              </Link>
            </Col>

            <Col span={24} style={{ height: screens.xs ? 30 : 'auto' }}>
              <RegistrationSteps currentStep={currentStep} />
            </Col>

            {!showForm ? (
              <Col span={24}>
                <MyButton
                  type='primary'
                  color='orange'
                  style={{ width: '100%', fontSize: '18px' }}
                  onClick={() => setMethodModalOpen(true)}
                >
                  {t("register") || "Регистрация"}
                </MyButton>

                <Flex justify="center" style={{ marginTop: 16 }}>
                  <MyText color='#0A033C' size={20} lineHeight={'32px'}>
                    {t("haveAccount")}
                  </MyText>
                  <Link to={embeddedLink("/auth")}>
                    <MyText color="rgba(0, 141, 210, 1)" size={20} style={{ marginLeft: 5 }} lineHeight={'32px'}>
                      {t("login")}
                    </MyText>
                  </Link>
                </Flex>
              </Col>
            ) : (
              <Col span={24}>
                <Form form={form}>
                  <Row>
                    <Col span={24}>
                      <MyText color="rgba(76, 76, 77, 1)" size={18} lineHeight={'24px'} weight={600}>
                        {isAnonymous ? `${t("enterPhoneNumber")}` : `${t("enterEmail")}`}
                      </MyText>

                      <Form.Item
                        name={isAnonymous ? "phone" : "email"}
                        labelCol={{ span: 24 }}
                        style={{ marginBottom: 8, marginTop: 8 }}
                        rules={[{ required: true, message: isAnonymous ? t("enterPhoneNumber") : t("enterEmail") }]}
                        validateStatus={registerDataState?.data?.errors?.some((err) => err.field === "username") ? "error" : ""}
                        help={registerDataState?.data?.errors?.find((err) => err.field === "username")?.message}
                      >
                        {isAnonymous ? (
                          <InputMask
                            mask="+7 (799) 999-99-99"
                            value={formData.phone}
                            onChange={handleInputChange}
                            maskChar=""
                          >
                            {(inputProps) => (
                              <Input
                                {...inputProps}
                                name="phone"
                                prefix={<PhoneOutlined style={{ color: "#969BAB", marginRight: 8 }} />}
                                placeholder="+7 (777) 777-77-77"
                              />
                            )}
                          </InputMask>
                        ) : (
                          <Input
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            prefix={<MailOutlined style={{ color: "#969BAB", marginRight: 8 }} />}
                            placeholder={t("enterEmail")}
                          />
                        )}
                      </Form.Item>

                      <Form.Item name="anonymous" valuePropName="checked" style={{ marginBottom: 8, marginTop: 16 }}>
                        <StyledCheckbox onChange={handleCheckboxChange}>{t("noEmail")}</StyledCheckbox>
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <MyText color="rgba(76, 76, 77, 1)" size={18} lineHeight={'24px'} weight={600}>
                        {t("createPassword")}
                      </MyText>
                      <Form.Item
                        name="password"
                        labelCol={{ span: 24 }}
                        style={{ marginTop: 8 }}
                        rules={[
                          { required: true, message: t("enterPassword") },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
                              if (value && !passwordRegex.test(value)) {
                                return Promise.reject(t("passwordRegex"));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Input.Password
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          prefix={<KeyOutlined style={{ color: '#969BAB', marginRight: 8 }} />}
                          placeholder={t("enterPassword")}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <MyText color="rgba(76, 76, 77, 1)" size={18} lineHeight={'24px'} weight={600}>
                        {t("repeatPassword")}
                      </MyText>
                      <Form.Item labelCol={{ span: 24 }} style={{ marginTop: 8 }} rules={[{ required: true, message: t("repeatPassword") }]}>
                        <Input.Password
                          name="password_repeat"
                          value={formData.password_repeat}
                          onChange={handleInputChange}
                          prefix={<KeyOutlined style={{ color: '#969BAB', marginRight: 8 }} />}
                          placeholder={t("enterPassword")}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item>
                        <MyButton
                          type='primary'
                          htmlType="submit"
                          color='orange'
                          style={{ width: '100%', fontSize: '18px' }}
                          disabled={!isFormComplete}
                          onClick={handleRegister}
                        >
                          {t("continue")} →
                        </MyButton>
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Flex justify="center">
                        <Form.Item>
                          <MyText color='#0A033C' size={20} lineHeight={'32px'}>
                            {t("haveAccount")}
                          </MyText>
                          <Link to={embeddedLink("/auth")}>
                            <MyText color="rgba(0, 141, 210, 1)" size={20} style={{ marginLeft: 5 }} lineHeight={'32px'}>
                              {t("login")}
                            </MyText>
                          </Link>
                        </Form.Item>
                      </Flex>
                    </Col>
                  </Row>
                </Form>
              </Col>
            )}
          </Row>
        </Flex>
      </StyledAuth>
    </>
  );
};
